import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  IconButton,
  Tooltip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Upload as UploadIcon,
  Send as SendIcon,
  FileUpload as FileUploadIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  SmartToy as RobotIcon,
} from '@mui/icons-material';

interface AnalysisFile {
  id: string;
  name: string;
  type: string;
  size: number;
  content?: string;
  preview?: string;
}

interface AnalysisResult {
  id: string;
  timestamp: Date;
  fileName: string;
  analysis: string;
  recommendations: string[];
  confidence: number;
  status: 'success' | 'warning' | 'error';
}

interface ChatMessage {
  id: string;
  timestamp: Date;
  type: 'user' | 'ai';
  content: string;
  isTyping?: boolean;
}

const AIAssistant: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [uploadedFiles, setUploadedFiles] = useState<AnalysisFile[]>([]);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<AnalysisFile | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [analysisText, setAnalysisText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Состояние для чата
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'analysis'>('chat');

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          const newFile: AnalysisFile = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            name: file.name,
            type: file.type,
            size: file.size,
            content: content,
            preview: file.type.startsWith('image/') ? content : undefined
          };
          setUploadedFiles(prev => [...prev, newFile]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const handleAnalyzeFile = async (file: AnalysisFile) => {
    setIsAnalyzing(true);
    setSelectedFile(file);
    
    // Интеллектуальный анализ с помощью ИИ
    setTimeout(() => {
      const intelligentAnalysis = generateIntelligentAnalysis(file);
      const newResult: AnalysisResult = {
        id: Date.now().toString(),
        timestamp: new Date(),
        fileName: file.name,
        analysis: intelligentAnalysis.analysis,
        recommendations: intelligentAnalysis.recommendations,
        confidence: intelligentAnalysis.confidence,
        status: intelligentAnalysis.status
      };
      
      setAnalysisResults(prev => [newResult, ...prev]);
      setIsAnalyzing(false);
      setShowResults(true);
    }, 2000);
  };

  const generateIntelligentAnalysis = (file: AnalysisFile, textContent?: string): { analysis: string; recommendations: string[]; confidence: number; status: 'success' | 'warning' | 'error' } => {
    const content = textContent || file.content || '';
    const fileName = file.name.toLowerCase();
    
    try {
      // Очистка и нормализация текста
      const cleanContent = content
        .replace(/\s+/g, ' ')
        .replace(/[^\w\s.,:;()\-+]/g, '')
        .trim()
        .toLowerCase();

      // Анализ по типу файла и содержимому с улучшенными паттернами
      if (fileName.includes('кровь') || fileName.includes('общий анализ') || 
          cleanContent.includes('гемоглобин') || cleanContent.includes('эритроциты') ||
          cleanContent.includes('лейкоциты') || cleanContent.includes('тромбоциты') ||
          cleanContent.includes('соэ') || cleanContent.includes('mcv') || 
          cleanContent.includes('mch') || cleanContent.includes('rdw')) {
        return analyzeBloodTest(content);
      } else if (fileName.includes('моча') || cleanContent.includes('лейкоциты') || 
                 cleanContent.includes('белок в моче') || cleanContent.includes('глюкоза') ||
                 cleanContent.includes('кетоны') || cleanContent.includes('нитриты') ||
                 cleanContent.includes('цилиндры') || cleanContent.includes('кристаллы')) {
        return analyzeUrineTest(content);
      } else if (fileName.includes('биохимия') || cleanContent.includes('алт') || 
                 cleanContent.includes('аст') || cleanContent.includes('билирубин') ||
                 cleanContent.includes('креатинин') || cleanContent.includes('мочевина') ||
                 cleanContent.includes('холестерин') || cleanContent.includes('триглицериды')) {
        return analyzeBiochemistry(content);
      } else if (fileName.includes('рентген') || fileName.includes('узи') || 
                 cleanContent.includes('рентгенологически') || cleanContent.includes('кт') ||
                 cleanContent.includes('мрт') || cleanContent.includes('флюорография')) {
        return analyzeImaging(content);
      } else if (fileName.includes('экг') || cleanContent.includes('ритм') || 
                 cleanContent.includes('сердце') || cleanContent.includes('пульс') ||
                 cleanContent.includes('давление') || cleanContent.includes('артериальное')) {
        return analyzeECG(content);
      } else {
        return analyzeGeneralMedicalContent(content);
      }
    } catch (error) {
      console.error('Ошибка при анализе:', error);
      return {
        analysis: "❌ Ошибка при анализе данных. Пожалуйста, проверьте формат введенного текста.",
        recommendations: [
          "Убедитесь, что текст содержит медицинские данные",
          "Проверьте правильность написания медицинских терминов",
          "Попробуйте ввести данные в другом формате"
        ],
        confidence: 0,
        status: 'error'
      };
    }
  };

  const analyzeBloodTest = (content: string) => {
    // Расширенные паттерны для поиска показателей
    const patterns = {
      hb: /(?:гемоглобин|Hb|HGB|гемоглобин)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      wbc: /(?:лейкоциты|WBC|белые клетки|лейкоциты)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      rbc: /(?:эритроциты|RBC|красные клетки|эритроциты)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      esr: /(?:СОЭ|ESR|скорость оседания)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      plt: /(?:тромбоциты|PLT|тромбоциты)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      hct: /(?:гематокрит|HCT|гематокрит)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      mcv: /(?:MCV|средний объем эритроцитов)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      mch: /(?:MCH|среднее содержание гемоглобина)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      mchc: /(?:MCHC|средняя концентрация гемоглобина)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      rdw: /(?:RDW|ширина распределения эритроцитов)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      mpv: /(?:MPV|средний объем тромбоцитов)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      pct: /(?:PCT|тромбокрит)[\s:]*(\d+(?:[.,]\d+)?)/gi,
      pdw: /(?:PDW|ширина распределения тромбоцитов)[\s:]*(\d+(?:[.,]\d+)?)/gi
    };

    // Извлечение значений с обработкой десятичных разделителей
    const extractValue = (pattern: RegExp): number | null => {
      const match = pattern.exec(content);
      if (match) {
        return parseFloat(match[1].replace(',', '.'));
      }
      return null;
    };

    const values = {
      hb: extractValue(patterns.hb),
      wbc: extractValue(patterns.wbc),
      rbc: extractValue(patterns.rbc),
      esr: extractValue(patterns.esr),
      plt: extractValue(patterns.plt),
      hct: extractValue(patterns.hct),
      mcv: extractValue(patterns.mcv),
      mch: extractValue(patterns.mch),
      mchc: extractValue(patterns.mchc),
      rdw: extractValue(patterns.rdw),
      mpv: extractValue(patterns.mpv),
      pct: extractValue(patterns.pct),
      pdw: extractValue(patterns.pdw)
    };
    
    let analysis = "📊 Общий анализ крови (расширенный):\n\n";
    let recommendations: string[] = [];
    let confidence = 95;
    let status: 'success' | 'warning' | 'error' = 'success';
    let criticalIssues = 0;
    let warningIssues = 0;

    // Анализ гемоглобина
    if (values.hb !== null) {
      if (values.hb < 90) {
        analysis += `🔴 Гемоглобин критически снижен (${values.hb} г/л) - тяжелая анемия\n`;
        recommendations.push("🚨 СРОЧНО: Консультация гематолога, переливание крови при Hb < 70 г/л");
        recommendations.push("Исследовать: железо, ферритин, В12, фолиевая кислота, ретикулоциты");
        status = 'error';
        criticalIssues++;
      } else if (values.hb < 110) {
        analysis += `🟡 Гемоглобин снижен (${values.hb} г/л) - анемия\n`;
        recommendations.push("Исследовать уровень железа, ферритина, В12, фолиевой кислоты");
        recommendations.push("Консультация гематолога при необходимости");
        status = 'warning';
        warningIssues++;
      } else if (values.hb > 180) {
        analysis += `🔴 Гемоглобин критически повышен (${values.hb} г/л) - эритроцитоз\n`;
        recommendations.push("🚨 СРОЧНО: Исключить полицитемию, заболевания легких, сердца");
        recommendations.push("Консультация гематолога, исследование ЭПО");
        status = 'error';
        criticalIssues++;
      } else if (values.hb > 160) {
        analysis += `🟡 Гемоглобин повышен (${values.hb} г/л) - возможен эритроцитоз\n`;
        recommendations.push("Исключить дегидратацию, заболевания легких, сердца");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ Гемоглобин в норме (${values.hb} г/л)\n`;
      }
    }

    // Анализ лейкоцитов
    if (values.wbc !== null) {
      if (values.wbc < 2) {
        analysis += `🔴 Лейкоциты критически снижены (${values.wbc} ×10⁹/л) - агранулоцитоз\n`;
        recommendations.push("🚨 СРОЧНО: Изоляция, профилактика инфекций, консультация гематолога");
        status = 'error';
        criticalIssues++;
      } else if (values.wbc < 4) {
        analysis += `🟡 Лейкоциты снижены (${values.wbc} ×10⁹/л) - лейкопения\n`;
        recommendations.push("Исключить вирусные инфекции, аутоиммунные заболевания");
        recommendations.push("Повторить анализ через 1-2 недели");
        status = 'warning';
        warningIssues++;
      } else if (values.wbc > 20) {
        analysis += `🔴 Лейкоциты критически повышены (${values.wbc} ×10⁹/л) - лейкемоидная реакция\n`;
        recommendations.push("🚨 СРОЧНО: Исключить лейкоз, консультация гематолога");
        status = 'error';
        criticalIssues++;
      } else if (values.wbc > 10) {
        analysis += `🟡 Лейкоциты повышены (${values.wbc} ×10⁹/л) - лейкоцитоз\n`;
        recommendations.push("Исключить бактериальную инфекцию, воспалительный процесс");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ Лейкоциты в норме (${values.wbc} ×10⁹/л)\n`;
      }
    }

    // Анализ тромбоцитов
    if (values.plt !== null) {
      if (values.plt < 50) {
        analysis += `🔴 Тромбоциты критически снижены (${values.plt} ×10⁹/л) - тромбоцитопения\n`;
        recommendations.push("🚨 СРОЧНО: Исключить кровотечения, консультация гематолога");
        status = 'error';
        criticalIssues++;
      } else if (values.plt < 150) {
        analysis += `🟡 Тромбоциты снижены (${values.plt} ×10⁹/л) - тромбоцитопения\n`;
        recommendations.push("Исключить аутоиммунные заболевания, инфекции");
        status = 'warning';
        warningIssues++;
      } else if (values.plt > 600) {
        analysis += `🔴 Тромбоциты критически повышены (${values.plt} ×10⁹/л) - тромбоцитоз\n`;
        recommendations.push("🚨 СРОЧНО: Исключить миелопролиферативные заболевания");
        status = 'error';
        criticalIssues++;
      } else if (values.plt > 450) {
        analysis += `🟡 Тромбоциты повышены (${values.plt} ×10⁹/л) - тромбоцитоз\n`;
        recommendations.push("Исключить воспалительные процессы, железодефицит");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ Тромбоциты в норме (${values.plt} ×10⁹/л)\n`;
      }
    }

    // Анализ СОЭ
    if (values.esr !== null) {
      if (values.esr > 50) {
        analysis += `🔴 СОЭ критически повышена (${values.esr} мм/ч) - воспалительный процесс\n`;
        recommendations.push("🚨 СРОЧНО: Исключить сепсис, аутоиммунные заболевания");
        status = 'error';
        criticalIssues++;
      } else if (values.esr > 20) {
        analysis += `🟡 СОЭ повышена (${values.esr} мм/ч) - воспалительный процесс\n`;
        recommendations.push("Исключить инфекции, аутоиммунные заболевания");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ СОЭ в норме (${values.esr} мм/ч)\n`;
      }
    }

    // Анализ эритроцитарных индексов
    if (values.mcv !== null) {
      if (values.mcv < 80) {
        analysis += `🟡 MCV снижен (${values.mcv} фл) - микроцитоз, возможна железодефицитная анемия\n`;
        recommendations.push("Исследовать уровень железа, ферритина");
        status = 'warning';
        warningIssues++;
      } else if (values.mcv > 100) {
        analysis += `🟡 MCV повышен (${values.mcv} фл) - макроцитоз, возможен дефицит В12/фолиевой кислоты\n`;
        recommendations.push("Исследовать уровень В12, фолиевой кислоты");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ MCV в норме (${values.mcv} фл)\n`;
      }
    }

    // Определение итогового статуса
    if (criticalIssues > 0) {
      status = 'error';
      confidence = 98;
    } else if (warningIssues > 0) {
      status = 'warning';
      confidence = 90;
    } else {
      status = 'success';
      confidence = 85;
    }

    // Добавление общих рекомендаций
    if (recommendations.length === 0) {
      recommendations.push("✅ Анализ в пределах нормы");
      recommendations.push("🔄 Повторить через 6-12 месяцев");
    } else {
      recommendations.push("📋 Контрольный анализ через 2-4 недели");
    }

    return { analysis, recommendations, confidence, status };
  };

  const analyzeUrineTest = (content: string) => {
    // Расширенные паттерны для анализа мочи
    const patterns = {
      protein: /(?:белок|protein|протеин)[\s:]*(\d+[.,]?\d*)/gi,
      wbc: /(?:лейкоциты|WBC|белые клетки|лейкоциты)[\s:]*(\d+)/gi,
      rbc: /(?:эритроциты|RBC|красные клетки|эритроциты)[\s:]*(\d+)/gi,
      bacteria: /(?:бактерии|bacteria|микрофлора|флора)/gi,
      glucose: /(?:глюкоза|glucose|сахар)[\s:]*(\d+[.,]?\d*)/gi,
      ketones: /(?:кетоны|ketones|ацетон)/gi,
      bilirubin: /(?:билирубин|bilirubin)/gi,
      urobilinogen: /(?:уробилиноген|urobilinogen)/gi,
      nitrites: /(?:нитриты|nitrites)/gi,
      ph: /(?:pH|кислотность)[\s:]*(\d+[.,]?\d*)/gi,
      density: /(?:плотность|density|удельный вес)[\s:]*(\d+[.,]?\d*)/gi,
      casts: /(?:цилиндры|casts|гиалиновые)/gi,
      crystals: /(?:кристаллы|crystals|оксалаты|ураты)/gi,
      epithelial: /(?:эпителий|epithelial|плоский эпителий)/gi
    };

    const extractValue = (pattern: RegExp): number | null => {
      const match = pattern.exec(content);
      if (match) {
        return parseFloat(match[1].replace(',', '.'));
      }
      return null;
    };

    const extractPresence = (pattern: RegExp): boolean => {
      return pattern.test(content);
    };

    const values = {
      protein: extractValue(patterns.protein),
      wbc: extractValue(patterns.wbc),
      rbc: extractValue(patterns.rbc),
      glucose: extractValue(patterns.glucose),
      ph: extractValue(patterns.ph),
      density: extractValue(patterns.density),
      bacteria: extractPresence(patterns.bacteria),
      ketones: extractPresence(patterns.ketones),
      bilirubin: extractPresence(patterns.bilirubin),
      urobilinogen: extractPresence(patterns.urobilinogen),
      nitrites: extractPresence(patterns.nitrites),
      casts: extractPresence(patterns.casts),
      crystals: extractPresence(patterns.crystals),
      epithelial: extractPresence(patterns.epithelial)
    };
    
    let analysis = "🧪 Общий анализ мочи (расширенный):\n\n";
    let recommendations: string[] = [];
    let confidence = 90;
    let status: 'success' | 'warning' | 'error' = 'success';
    let criticalIssues = 0;
    let warningIssues = 0;

    // Анализ протеинурии
    if (values.protein !== null) {
      if (values.protein > 3.0) {
        analysis += `🔴 Массивная протеинурия (${values.protein} г/л) - нефротический синдром\n`;
        recommendations.push("🚨 СРОЧНО: Консультация нефролога, госпитализация");
        recommendations.push("Исследовать суточную протеинурию, альбумин сыворотки");
        status = 'error';
        criticalIssues++;
      } else if (values.protein > 0.5) {
        analysis += `🔴 Протеинурия (${values.protein} г/л) - заболевания почек\n`;
        recommendations.push("🚨 СРОЧНО: Консультация нефролога");
        recommendations.push("Исследовать суточную протеинурию, креатинин, СКФ");
        status = 'error';
        criticalIssues++;
      } else if (values.protein > 0.14) {
        analysis += `🟡 Микроальбуминурия (${values.protein} г/л) - раннее поражение почек\n`;
        recommendations.push("Консультация нефролога");
        recommendations.push("Исследовать суточную протеинурию, HbA1c");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ Белок в норме (${values.protein} г/л)\n`;
      }
    }

    // Анализ лейкоцитурии
    if (values.wbc !== null) {
      if (values.wbc > 50) {
        analysis += `🔴 Массивная лейкоцитурия (${values.wbc} в поле зрения) - пиурия\n`;
        recommendations.push("🚨 СРОЧНО: Посев мочи, консультация уролога");
        recommendations.push("Антибактериальная терапия по результатам посева");
        status = 'error';
        criticalIssues++;
      } else if (values.wbc > 10) {
        analysis += `🔴 Лейкоцитурия (${values.wbc} в поле зрения) - воспалительный процесс\n`;
        recommendations.push("Посев мочи на флору и чувствительность");
        recommendations.push("Консультация уролога");
        status = 'error';
        criticalIssues++;
      } else if (values.wbc > 5) {
        analysis += `🟡 Лейкоцитурия (${values.wbc} в поле зрения) - возможная инфекция\n`;
        recommendations.push("Посев мочи на флору");
        recommendations.push("Повторить анализ через 3-5 дней");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ Лейкоциты в норме (${values.wbc} в поле зрения)\n`;
      }
    }

    // Анализ гематурии
    if (values.rbc !== null) {
      if (values.rbc > 50) {
        analysis += `🔴 Массивная гематурия (${values.rbc} в поле зрения) - кровотечение\n`;
        recommendations.push("🚨 СРОЧНО: Консультация уролога, цистоскопия");
        recommendations.push("Исключить опухоли мочевого пузыря, почек");
        status = 'error';
        criticalIssues++;
      } else if (values.rbc > 10) {
        analysis += `🔴 Гематурия (${values.rbc} в поле зрения) - заболевания почек/мочевого пузыря\n`;
        recommendations.push("Консультация уролога/нефролога");
        recommendations.push("УЗИ почек и мочевого пузыря, цистоскопия");
        status = 'error';
        criticalIssues++;
      } else if (values.rbc > 3) {
        analysis += `🟡 Микрогематурия (${values.rbc} в поле зрения) - возможны заболевания почек\n`;
        recommendations.push("УЗИ почек и мочевого пузыря");
        recommendations.push("Повторить анализ через 1-2 недели");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ Эритроциты в норме (${values.rbc} в поле зрения)\n`;
      }
    }

    // Анализ глюкозурии
    if (values.glucose !== null && values.glucose > 0) {
      analysis += `🔴 Глюкозурия (${values.glucose} ммоль/л) - сахарный диабет\n`;
      recommendations.push("🚨 СРОЧНО: Консультация эндокринолога");
      recommendations.push("Исследовать глюкозу крови, HbA1c");
      status = 'error';
      criticalIssues++;
    }

    // Анализ кетонурии
    if (values.ketones) {
      analysis += `🔴 Кетонурия - диабетический кетоацидоз или голодание\n`;
      recommendations.push("🚨 СРОЧНО: Консультация эндокринолога");
      recommendations.push("Исследовать глюкозу крови, кетоны крови");
      status = 'error';
      criticalIssues++;
    }

    // Анализ бактериурии
    if (values.bacteria) {
      analysis += `🟡 Бактериурия - инфекция мочевыводящих путей\n`;
      recommendations.push("Посев мочи на флору и чувствительность");
      recommendations.push("Консультация уролога при рецидивах");
      status = 'warning';
      warningIssues++;
    }

    // Анализ нитритов
    if (values.nitrites) {
      analysis += `🟡 Нитриты положительные - бактериальная инфекция\n`;
      recommendations.push("Посев мочи на флору");
      recommendations.push("Антибактериальная терапия");
      status = 'warning';
      warningIssues++;
    }

    // Анализ цилиндров
    if (values.casts) {
      analysis += `🔴 Цилиндры в моче - поражение почечных канальцев\n`;
      recommendations.push("🚨 СРОЧНО: Консультация нефролога");
      recommendations.push("Исследовать креатинин, СКФ, УЗИ почек");
      status = 'error';
      criticalIssues++;
    }

    // Анализ кристаллов
    if (values.crystals) {
      analysis += `🟡 Кристаллы в моче - возможна мочекаменная болезнь\n`;
      recommendations.push("УЗИ почек и мочевого пузыря");
      recommendations.push("Анализ камней при их наличии");
      status = 'warning';
      warningIssues++;
    }

    // Определение итогового статуса
    if (criticalIssues > 0) {
      status = 'error';
      confidence = 95;
    } else if (warningIssues > 0) {
      status = 'warning';
      confidence = 85;
    } else {
      status = 'success';
      confidence = 80;
    }

    // Добавление общих рекомендаций
    if (recommendations.length === 0) {
      recommendations.push("✅ Анализ мочи в норме");
      recommendations.push("🔄 Повторить через 6-12 месяцев");
    } else {
      recommendations.push("📋 Контрольный анализ через 1-2 недели");
    }

    return { analysis, recommendations, confidence, status };
  };

  const analyzeBiochemistry = (content: string) => {
    // Расширенные паттерны для биохимического анализа
    const patterns = {
      alt: /(?:алт|alt|аланинаминотрансфераза)[\s:]*(\d+[.,]?\d*)/gi,
      ast: /(?:аст|ast|аспартатаминотрансфераза)[\s:]*(\d+[.,]?\d*)/gi,
      bilirubin: /(?:билирубин|bilirubin|общий билирубин)[\s:]*(\d+[.,]?\d*)/gi,
      glucose: /(?:глюкоза|glucose|сахар)[\s:]*(\d+[.,]?\d*)/gi,
      creatinine: /(?:креатинин|creatinine|креатин)[\s:]*(\d+[.,]?\d*)/gi,
      urea: /(?:мочевина|urea|азот мочевины)[\s:]*(\d+[.,]?\d*)/gi,
      cholesterol: /(?:холестерин|cholesterol|общий холестерин)[\s:]*(\d+[.,]?\d*)/gi,
      triglycerides: /(?:триглицериды|triglycerides|тг)[\s:]*(\d+[.,]?\d*)/gi,
      hdl: /(?:хс-лпвп|hdl|холестерин лпвп)[\s:]*(\d+[.,]?\d*)/gi,
      ldl: /(?:хс-лпнп|ldl|холестерин лпнп)[\s:]*(\d+[.,]?\d*)/gi,
      protein: /(?:общий белок|total protein|белок общий)[\s:]*(\d+[.,]?\d*)/gi,
      albumin: /(?:альбумин|albumin)[\s:]*(\d+[.,]?\d*)/gi,
      calcium: /(?:кальций|calcium|ca)[\s:]*(\d+[.,]?\d*)/gi,
      phosphorus: /(?:фосфор|phosphorus|p)[\s:]*(\d+[.,]?\d*)/gi,
      sodium: /(?:натрий|sodium|na)[\s:]*(\d+[.,]?\d*)/gi,
      potassium: /(?:калий|potassium|k)[\s:]*(\d+[.,]?\d*)/gi,
      chloride: /(?:хлориды|chloride|cl)[\s:]*(\d+[.,]?\d*)/gi,
      uricAcid: /(?:мочевая кислота|uric acid|ураты)[\s:]*(\d+[.,]?\d*)/gi,
      ck: /(?:кфк|ck|креатинкиназа)[\s:]*(\d+[.,]?\d*)/gi,
      ldh: /(?:лдг|ldh|лактатдегидрогеназа)[\s:]*(\d+[.,]?\d*)/gi,
      amylase: /(?:амилаза|amylase)[\s:]*(\d+[.,]?\d*)/gi,
      lipase: /(?:липаза|lipase)[\s:]*(\d+[.,]?\d*)/gi
    };

    const extractValue = (pattern: RegExp): number | null => {
      const match = pattern.exec(content);
      if (match) {
        return parseFloat(match[1].replace(',', '.'));
      }
      return null;
    };

    const values = {
      alt: extractValue(patterns.alt),
      ast: extractValue(patterns.ast),
      bilirubin: extractValue(patterns.bilirubin),
      glucose: extractValue(patterns.glucose),
      creatinine: extractValue(patterns.creatinine),
      urea: extractValue(patterns.urea),
      cholesterol: extractValue(patterns.cholesterol),
      triglycerides: extractValue(patterns.triglycerides),
      hdl: extractValue(patterns.hdl),
      ldl: extractValue(patterns.ldl),
      protein: extractValue(patterns.protein),
      albumin: extractValue(patterns.albumin),
      calcium: extractValue(patterns.calcium),
      phosphorus: extractValue(patterns.phosphorus),
      sodium: extractValue(patterns.sodium),
      potassium: extractValue(patterns.potassium),
      chloride: extractValue(patterns.chloride),
      uricAcid: extractValue(patterns.uricAcid),
      ck: extractValue(patterns.ck),
      ldh: extractValue(patterns.ldh),
      amylase: extractValue(patterns.amylase),
      lipase: extractValue(patterns.lipase)
    };

    let analysis = "🧪 Биохимический анализ крови (расширенный):\n\n";
    let recommendations: string[] = [];
    let confidence = 95;
    let status: 'success' | 'warning' | 'error' = 'success';
    let criticalIssues = 0;
    let warningIssues = 0;
    
    // Анализ печеночных ферментов
    if (values.alt !== null) {
      if (values.alt > 200) {
        analysis += `🔴 АЛТ критически повышена (${values.alt} Ед/л) - тяжелое поражение печени\n`;
        recommendations.push("🚨 СРОЧНО: Консультация гепатолога, госпитализация");
        recommendations.push("Исключить вирусные гепатиты, токсическое поражение");
        status = 'error';
        criticalIssues++;
      } else if (values.alt > 80) {
        analysis += `🔴 АЛТ повышена (${values.alt} Ед/л) - поражение печени\n`;
        recommendations.push("🚨 СРОЧНО: Консультация гастроэнтеролога");
        recommendations.push("Исключить вирусные гепатиты, УЗИ печени");
        status = 'error';
        criticalIssues++;
      } else if (values.alt > 40) {
        analysis += `🟡 АЛТ повышена (${values.alt} Ед/л) - возможное поражение печени\n`;
        recommendations.push("Исключить вирусные гепатиты");
        recommendations.push("УЗИ печени и желчного пузыря");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ АЛТ в норме (${values.alt} Ед/л)\n`;
      }
    }

    if (values.ast !== null) {
      if (values.ast > 200) {
        analysis += `🔴 АСТ критически повышена (${values.ast} Ед/л) - тяжелое поражение\n`;
        recommendations.push("🚨 СРОЧНО: Исключить инфаркт миокарда, поражение печени");
        recommendations.push("ЭКГ, тропонины, консультация кардиолога");
        status = 'error';
        criticalIssues++;
      } else if (values.ast > 80) {
        analysis += `🔴 АСТ повышена (${values.ast} Ед/л) - поражение печени или сердца\n`;
        recommendations.push("🚨 СРОЧНО: Исключить инфаркт миокарда");
        recommendations.push("ЭКГ, тропонины, УЗИ сердца");
        status = 'error';
        criticalIssues++;
      } else if (values.ast > 40) {
        analysis += `🟡 АСТ повышена (${values.ast} Ед/л) - возможное поражение\n`;
        recommendations.push("Исключить инфаркт миокарда");
        recommendations.push("ЭКГ, тропонины");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ АСТ в норме (${values.ast} Ед/л)\n`;
      }
    }

    // Анализ билирубина
    if (values.bilirubin !== null) {
      if (values.bilirubin > 100) {
        analysis += `🔴 Билирубин критически повышен (${values.bilirubin} мкмоль/л) - тяжелая желтуха\n`;
        recommendations.push("🚨 СРОЧНО: Консультация гепатолога, госпитализация");
        recommendations.push("Исключить обструкцию желчных путей, гемолиз");
        status = 'error';
        criticalIssues++;
      } else if (values.bilirubin > 50) {
        analysis += `🔴 Билирубин повышена (${values.bilirubin} мкмоль/л) - желтуха\n`;
        recommendations.push("🚨 СРОЧНО: Консультация гастроэнтеролога");
        recommendations.push("Исключить гепатиты, желчнокаменную болезнь");
        status = 'error';
        criticalIssues++;
      } else if (values.bilirubin > 20.5) {
        analysis += `🟡 Билирубин повышена (${values.bilirubin} мкмоль/л) - возможная желтуха\n`;
        recommendations.push("Исключить гепатиты, желчнокаменную болезнь");
        recommendations.push("УЗИ печени и желчных путей");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ Билирубин в норме (${values.bilirubin} мкмоль/л)\n`;
      }
    }

    // Анализ глюкозы
    if (values.glucose !== null) {
      if (values.glucose > 15) {
        analysis += `🔴 Глюкоза критически повышена (${values.glucose} ммоль/л) - гипергликемический криз\n`;
        recommendations.push("🚨 СРОЧНО: Консультация эндокринолога, госпитализация");
        recommendations.push("Исключить диабетический кетоацидоз");
        status = 'error';
        criticalIssues++;
      } else if (values.glucose > 11.1) {
        analysis += `🔴 Глюкоза повышена (${values.glucose} ммоль/л) - сахарный диабет\n`;
        recommendations.push("🚨 СРОЧНО: Консультация эндокринолога");
        recommendations.push("HbA1c, глюкозотолерантный тест");
        status = 'error';
        criticalIssues++;
      } else if (values.glucose > 6.1) {
        analysis += `🟡 Глюкоза повышена (${values.glucose} ммоль/л) - гипергликемия\n`;
        recommendations.push("Консультация эндокринолога");
        recommendations.push("HbA1c, глюкозотолерантный тест");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ Глюкоза в норме (${values.glucose} ммоль/л)\n`;
      }
    }

    // Анализ креатинина
    if (values.creatinine !== null) {
      if (values.creatinine > 200) {
        analysis += `🔴 Креатинин критически повышен (${values.creatinine} мкмоль/л) - почечная недостаточность\n`;
        recommendations.push("🚨 СРОЧНО: Консультация нефролога, госпитализация");
        recommendations.push("Исключить острую почечную недостаточность");
        status = 'error';
        criticalIssues++;
      } else if (values.creatinine > 120) {
        analysis += `🔴 Креатинин повышена (${values.creatinine} мкмоль/л) - нарушение функции почек\n`;
        recommendations.push("🚨 СРОЧНО: Консультация нефролога");
        recommendations.push("Исследовать СКФ, УЗИ почек");
        status = 'error';
        criticalIssues++;
      } else if (values.creatinine > 100) {
        analysis += `🟡 Креатинин повышена (${values.creatinine} мкмоль/л) - возможное нарушение функции почек\n`;
        recommendations.push("Консультация нефролога");
        recommendations.push("Исследовать СКФ, УЗИ почек");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ Креатинин в норме (${values.creatinine} мкмоль/л)\n`;
      }
    }

    // Анализ холестерина
    if (values.cholesterol !== null) {
      if (values.cholesterol > 8) {
        analysis += `🔴 Холестерин критически повышен (${values.cholesterol} ммоль/л) - гиперхолестеринемия\n`;
        recommendations.push("🚨 СРОЧНО: Консультация кардиолога");
        recommendations.push("Исключить семейную гиперхолестеринемию");
        status = 'error';
        criticalIssues++;
      } else if (values.cholesterol > 6.5) {
        analysis += `🔴 Холестерин повышена (${values.cholesterol} ммоль/л) - гиперхолестеринемия\n`;
        recommendations.push("🚨 СРОЧНО: Консультация кардиолога");
        recommendations.push("Исследовать липидный профиль, УЗИ сосудов");
        status = 'error';
        criticalIssues++;
      } else if (values.cholesterol > 5.2) {
        analysis += `🟡 Холестерин повышена (${values.cholesterol} ммоль/л) - возможная гиперхолестеринемия\n`;
        recommendations.push("Консультация кардиолога");
        recommendations.push("Исследовать липидный профиль");
        status = 'warning';
        warningIssues++;
      } else {
        analysis += `✅ Холестерин в норме (${values.cholesterol} ммоль/л)\n`;
      }
    }

    // Определение итогового статуса
    if (criticalIssues > 0) {
      status = 'error';
      confidence = 98;
    } else if (warningIssues > 0) {
      status = 'warning';
      confidence = 90;
    } else {
      status = 'success';
      confidence = 85;
    }

    // Добавление общих рекомендаций
    if (recommendations.length === 0) {
      recommendations.push("✅ Биохимические показатели в норме");
      recommendations.push("🔄 Повторить через 6-12 месяцев");
    } else {
      recommendations.push("📋 Контрольный анализ через 2-4 недели");
    }

    return { analysis, recommendations, confidence, status };
  };

  const analyzeImaging = (content: string) => {
    let analysis = "Рентгенологическое исследование:\n";
    let recommendations: string[] = [];
    let confidence = 80;
    let status: 'success' | 'warning' | 'error' = 'success';
    
    if (content.includes('пневмония') || content.includes('инфильтрат')) {
      analysis += "• Выявлены признаки пневмонии\n";
      recommendations.push("Антибактериальная терапия");
      recommendations.push("Контрольный рентген через 2 недели");
      recommendations.push("Консультация пульмонолога");
      status = 'warning';
      confidence = 90;
    } else if (content.includes('норма') || content.includes('без патологии')) {
      analysis += "• Патологических изменений не выявлено\n";
      recommendations.push("Повторить исследование при необходимости");
      status = 'success';
      confidence = 85;
    } else {
      analysis += "• Требуется детальная оценка специалистом\n";
      recommendations.push("Консультация рентгенолога");
      recommendations.push("Дополнительные исследования при необходимости");
      status = 'warning';
      confidence = 70;
    }
    
    return { analysis, recommendations, confidence, status };
  };

  const analyzeECG = (content: string) => {
    let analysis = "Электрокардиография:\n";
    let recommendations: string[] = [];
    let confidence = 85;
    let status: 'success' | 'warning' | 'error' = 'success';
    
    if (content.includes('аритмия') || content.includes('тахикардия')) {
      analysis += "• Выявлены нарушения ритма\n";
      recommendations.push("Холтеровское мониторирование");
      recommendations.push("Консультация кардиолога");
      recommendations.push("Эхокардиография");
      status = 'warning';
      confidence = 90;
    } else if (content.includes('норма') || content.includes('синусовый ритм')) {
      analysis += "• Ритм синусовый, патологии не выявлено\n";
      recommendations.push("Повторить ЭКГ через год");
      status = 'success';
      confidence = 85;
    } else {
      analysis += "• Требуется интерпретация кардиолога\n";
      recommendations.push("Консультация кардиолога");
      recommendations.push("Дополнительные исследования при необходимости");
      status = 'warning';
      confidence = 75;
    }
    
    return { analysis, recommendations, confidence, status };
  };

  const analyzeGeneralMedicalContent = (content: string) => {
    let analysis = "Медицинский документ:\n";
    let recommendations: string[] = [];
    let confidence = 70;
    let status: 'success' | 'warning' | 'error' = 'success';
    
    // Поиск ключевых слов и патологий
    if (content.includes('норма') || content.includes('без патологии') || content.includes('здоров')) {
      analysis += "• Патологических изменений не выявлено\n";
      recommendations.push("Продолжить наблюдение");
      status = 'success';
      confidence = 80;
    } else if (content.includes('патология') || content.includes('отклонение') || content.includes('нарушение')) {
      analysis += "• Выявлены патологические изменения\n";
      recommendations.push("Консультация профильного специалиста");
      recommendations.push("Дополнительные исследования");
      status = 'warning';
      confidence = 75;
    } else {
      analysis += "• Требуется детальная оценка специалистом\n";
      recommendations.push("Консультация врача");
      recommendations.push("Уточнение диагноза");
      status = 'warning';
      confidence = 60;
    }
    
    return { analysis, recommendations, confidence, status };
  };

  const handleTextAnalysis = async () => {
    if (!analysisText.trim()) return;
    
    setIsAnalyzing(true);
    
    // Интеллектуальный анализ текста
    setTimeout(() => {
      const intelligentAnalysis = generateIntelligentAnalysis({ 
        id: 'text', 
        name: 'Текстовый анализ', 
        type: 'text/plain', 
        size: analysisText.length 
      }, analysisText);
      
      const newResult: AnalysisResult = {
        id: Date.now().toString(),
        timestamp: new Date(),
        fileName: 'Текстовый анализ',
        analysis: intelligentAnalysis.analysis,
        recommendations: intelligentAnalysis.recommendations,
        confidence: intelligentAnalysis.confidence,
        status: intelligentAnalysis.status
      };
      
      setAnalysisResults(prev => [newResult, ...prev]);
      setIsAnalyzing(false);
      setShowResults(true);
      setAnalysisText('');
    }, 2000);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const downloadResult = (result: AnalysisResult) => {
    const content = `Анализ: ${result.fileName}\n\nРезультат:\n${result.analysis}\n\nРекомендации:\n${result.recommendations.map((rec, i) => `${i + 1}. ${rec}`).join('\n')}\n\nУверенность: ${result.confidence}%\nДата: ${result.timestamp.toLocaleString()}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analysis_${result.fileName}_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Функции для чата
  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      timestamp: new Date(),
      type: 'user',
      content: chatInput.trim()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setIsTyping(true);
    
    // Имитация ответа ИИ
    setTimeout(() => {
      const aiResponse = generateAIResponse(chatInput.trim());
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        timestamp: new Date(),
        type: 'ai',
        content: aiResponse
      };
      
      setChatMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const generateAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();
    
    // Медицинские вопросы
    if (input.includes('температура') || input.includes('лихорадка')) {
      return `🌡️ **Лихорадка у детей**\n\nНормальная температура тела: 36,6-37,2°C\n\n**Причины повышения температуры:**\n• Вирусные инфекции (ОРВИ, грипп)\n• Бактериальные инфекции\n• Реакция на прививку\n• Перегрев\n\n**Что делать:**\n• При T >38,5°C дать жаропонижающее (парацетамол, ибупрофен)\n• Обеспечить обильное питье\n• Не укутывать ребенка\n• При T >39°C или ухудшении состояния - вызвать врача\n\n⚠️ **Срочно к врачу:**\n• Температура >39°C у детей до 3 месяцев\n• Судороги\n• Сыпь\n• Нарушение сознания`;
    }
    
    if (input.includes('кашель') || input.includes('кашляет')) {
      return `🫁 **Кашель у детей**\n\n**Виды кашля:**\n• **Сухой** - раздражение дыхательных путей\n• **Влажный** - скопление мокроты\n• **Лающий** - круп, ларингит\n• **Приступообразный** - коклюш\n\n**Лечение:**\n• Обильное теплое питье\n• Увлажнение воздуха\n• При влажном кашле - отхаркивающие средства\n• При сухом - противокашлевые (только по назначению врача)\n\n⚠️ **Срочно к врачу:**\n• Лающий кашель + одышка\n• Кашель с кровью\n• Приступы удушья\n• Температура >38,5°C`;
    }
    
    if (input.includes('сыпь') || input.includes('пятна') || input.includes('прыщи')) {
      return `🔍 **Сыпь у детей**\n\n**Частые причины:**\n• **Аллергическая реакция** - зудящие красные пятна\n• **Инфекционные заболевания** - корь, краснуха, ветрянка\n• **Потница** - мелкие розовые пузырьки\n• **Атопический дерматит** - сухие шелушащиеся пятна\n\n**Что делать:**\n• Не расчесывать\n• Одевать в хлопчатобумажную одежду\n• Использовать гипоаллергенные средства\n• При зуде - антигистаминные препараты\n\n⚠️ **Срочно к врачу:**\n• Сыпь + температура\n• Сыпь + рвота\n• Петехиальная сыпь (не исчезает при надавливании)\n• Быстрое распространение сыпи`;
    }
    
    if (input.includes('рвота') || input.includes('тошнит')) {
      return `🤮 **Рвота у детей**\n\n**Возможные причины:**\n• Пищевое отравление\n• Кишечная инфекция\n• Переедание\n• Укачивание\n• Повышенное внутричерепное давление\n\n**Что делать:**\n• Не кормить 2-4 часа\n• Давать по 1-2 чайные ложки воды каждые 10-15 минут\n• При улучшении - постепенно увеличивать количество жидкости\n• Соблюдать диету (рис, сухари, бананы)\n\n⚠️ **Срочно к врачу:**\n• Рвота + температура >38°C\n• Рвота + понос\n• Рвота с кровью\n• Рвота + сонливость\n• Рвота >6 часов`;
    }
    
    if (input.includes('понос') || input.includes('диарея')) {
      return `💩 **Диарея у детей**\n\n**Причины:**\n• Кишечные инфекции (вирусные, бактериальные)\n• Пищевое отравление\n• Непереносимость продуктов\n• Прием антибиотиков\n\n**Что делать:**\n• Обильное питье (регидрон, глюкосолан)\n• Диета: рис, бананы, яблоки, сухари\n• Продолжать грудное вскармливание\n• При тяжелом течении - в/в регидратация\n\n⚠️ **Срочно к врачу:**\n• Признаки обезвоживания (сухость губ, впалые глаза, редкое мочеиспускание)\n• Кровь в стуле\n• Температура >38,5°C\n• Рвота + понос\n• Отказ от питья`;
    }
    
    if (input.includes('прививка') || input.includes('вакцина')) {
      return `💉 **Вакцинация детей**\n\n**Национальный календарь прививок:**\n• **Гепатит В** - в роддоме, 1 месяц, 6 месяцев\n• **БЦЖ** - 3-7 дней\n• **АКДС** - 3, 4.5, 6, 18 месяцев\n• **Полиомиелит** - 3, 4.5, 6, 18 месяцев, 6 лет\n• **Корь, краснуха, паротит** - 12 месяцев, 6 лет\n\n**Противопоказания:**\n• Острое заболевание с температурой\n• Аллергия на компоненты вакцины\n• Иммунодефицит\n• Неврологические заболевания\n\n**После прививки:**\n• Наблюдать за ребенком 30 минут\n• При температуре >38,5°C - жаропонижающее\n• При покраснении места укола - холодный компресс`;
    }
    
    if (input.includes('рост') || input.includes('вес') || input.includes('развитие')) {
      return `📏 **Рост и развитие ребенка**\n\n**Нормы роста (ВОЗ):**\n• **0-6 месяцев:** +2,5-3 см/месяц\n• **6-12 месяцев:** +1,5-2 см/месяц\n• **1-2 года:** +10-12 см/год\n• **2-3 года:** +8-10 см/год\n\n**Нормы веса:**\n• **0-6 месяцев:** +600-800 г/месяц\n• **6-12 месяцев:** +300-500 г/месяц\n• **1-2 года:** +2-3 кг/год\n\n**Критерии нормального развития:**\n• Удерживает голову - 2-3 месяца\n• Сидит без поддержки - 6-8 месяцев\n• Ходит - 12-15 месяцев\n• Говорит первые слова - 12-18 месяцев\n\n⚠️ **К врачу при:**\n• Отставании в росте/весе\n• Задержке моторного развития\n• Потере веса`;
    }
    
    if (input.includes('сон') || input.includes('спит')) {
      return `😴 **Сон ребенка**\n\n**Нормы сна по возрасту:**\n• **0-3 месяца:** 14-17 часов\n• **4-11 месяцев:** 12-15 часов\n• **1-2 года:** 11-14 часов\n• **3-5 лет:** 10-13 часов\n\n**Проблемы со сном:**\n• **Ночные пробуждения** - норма до 6 месяцев\n• **Трудности засыпания** - ритуал перед сном\n• **Ранние подъемы** - затемнить комнату\n• **Кошмары** - успокоить, не будить\n\n**Советы:**\n• Режим дня\n• Ритуал перед сном\n• Комфортная температура (18-20°C)\n• Темная тихая комната\n• Не кормить перед сном`;
    }
    
    if (input.includes('питание') || input.includes('кормление') || input.includes('еда')) {
      return `🍼 **Питание ребенка**\n\n**Грудное вскармливание:**\n• По требованию до 6 месяцев\n• Исключительно грудное молоко до 6 месяцев\n• Введение прикорма с 6 месяцев\n• Продолжение ГВ до 2 лет и дольше\n\n**Прикорм:**\n• **6 месяцев:** овощи, каши\n• **7-8 месяцев:** мясо, фрукты\n• **9-12 месяцев:** рыба, яйца\n• **12+ месяцев:** общий стол\n\n**Принципы:**\n• Постепенное введение\n• Один продукт в неделю\n• Начинать с 1/2 чайной ложки\n• Наблюдать за реакцией\n• Не заставлять есть`;
    }
    
    // Общие медицинские вопросы
    if (input.includes('болит') || input.includes('боль')) {
      return `🩺 **Боль у ребенка**\n\n**Оценка боли:**\n• **0-3 года:** по поведению (плач, беспокойство)\n• **3-7 лет:** шкала лиц (покажите на картинке)\n• **7+ лет:** числовая шкала 0-10\n\n**Виды боли:**\n• **Острая** - внезапная, сильная\n• **Хроническая** - длительная, ноющая\n• **Функциональная** - без органической причины\n\n**Что делать:**\n• Успокоить ребенка\n• При сильной боли - обезболивающее (парацетамол, ибупрофен)\n• Холодный компресс при травме\n• Теплый компресс при спазме\n\n⚠️ **Срочно к врачу:**\n• Сильная боль в животе\n• Головная боль + рвота\n• Боль в груди\n• Боль в суставах + температура`;
    }
    
    if (input.includes('аллергия') || input.includes('аллергическая')) {
      return `🤧 **Аллергия у детей**\n\n**Частые аллергены:**\n• Пищевые (молоко, яйца, орехи, рыба)\n• Бытовые (пыль, шерсть животных)\n• Пыльцевые (цветение растений)\n• Лекарственные\n\n**Симптомы:**\n• Кожные: сыпь, зуд, покраснение\n• Респираторные: насморк, кашель, чихание\n• Желудочно-кишечные: рвота, понос, боли в животе\n• Системные: отек Квинке, анафилаксия\n\n**Лечение:**\n• Исключение аллергена\n• Антигистаминные препараты\n• Местные средства (мази, капли)\n• В тяжелых случаях - гормоны\n\n⚠️ **Срочно к врачу:**\n• Отек лица, губ, языка\n• Затрудненное дыхание\n• Сыпь по всему телу\n• Рвота + сыпь`;
    }
    
    // Если вопрос не распознан
    return `🤖 **ИИ-помощник**\n\nЯ могу помочь с вопросами о:\n• Температуре и лихорадке\n• Кашле и простуде\n• Сыпи и кожных проблемах\n• Рвоте и диарее\n• Прививках и вакцинации\n• Росте и развитии\n• Сне ребенка\n• Питании и кормлении\n• Болях различного характера\n• Аллергических реакциях\n\nЗадайте конкретный вопрос, и я дам подробный ответ с рекомендациями!`;
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box>
      <style>
        {`
          @keyframes pulse {
            0%, 80%, 100% {
              transform: scale(0);
              opacity: 0.5;
            }
            40% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PsychologyIcon sx={{ color: '#4A90E2', mr: 2, fontSize: 40 }} />
          <Box>
            <Typography variant="h1" sx={{ 
              mb: 1,
              background: 'linear-gradient(45deg, #4A90E2, #50C878)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700
            }}>
              ИИ-помощник
            </Typography>
            <Typography variant="h6" sx={{ color: '#666', fontWeight: 400 }}>
              Медицинский консультант и анализ документов
            </Typography>
          </Box>
        </Box>
        <Typography variant="body1" sx={{ color: '#718096', maxWidth: 800, fontSize: '1.1rem', lineHeight: 1.6 }}>
          Умный помощник для ответов на медицинские вопросы и анализа документов. 
          Задавайте вопросы о здоровье ребенка или загружайте результаты анализов для получения рекомендаций.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(74, 144, 226, 0.15)' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <InfoIcon sx={{ color: '#4A90E2', mr: 1, mt: 0.5 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Важная информация
            </Typography>
            <Typography variant="body2">
              ИИ-помощник предназначен для предварительной оценки результатов анализов. 
              Все выводы должны быть подтверждены врачом. Не заменяет профессиональную медицинскую консультацию.
            </Typography>
          </Box>
        </Box>
      </Alert>

      {/* Вкладки */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
      }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex' }}>
            <Button
              onClick={() => setActiveTab('chat')}
              sx={{
                flex: 1,
                py: 2,
                px: 3,
                borderRadius: 0,
                backgroundColor: activeTab === 'chat' ? '#4A90E2' : 'transparent',
                color: activeTab === 'chat' ? 'white' : '#666',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: activeTab === 'chat' ? '#357ABD' : '#F5F5F5'
                }
              }}
            >
              💬 Медицинский чат
            </Button>
            <Button
              onClick={() => setActiveTab('analysis')}
              sx={{
                flex: 1,
                py: 2,
                px: 3,
                borderRadius: 0,
                backgroundColor: activeTab === 'analysis' ? '#4A90E2' : 'transparent',
                color: activeTab === 'analysis' ? 'white' : '#666',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: activeTab === 'analysis' ? '#357ABD' : '#F5F5F5'
                }
              }}
            >
              📄 Анализ документов
            </Button>
          </Box>
        </Box>
      </Card>

      {activeTab === 'chat' ? (
        // Чат интерфейс
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '70vh' }}>
          <Card sx={{ 
            flex: 1,
            borderRadius: 3, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              p: 0,
              overflow: 'hidden'
            }}>
              {/* Сообщения чата */}
              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto', 
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}>
                {chatMessages.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    textAlign: 'center',
                    p: 3
                  }}>
                    <RobotIcon sx={{ fontSize: 64, color: '#4A90E2', mb: 2 }} />
                    <Typography variant="h5" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                      Добро пожаловать в ИИ-помощник!
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#718096', mb: 3 }}>
                      Задайте любой вопрос о здоровье ребенка, и я дам подробный ответ с рекомендациями
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                      {['У ребенка температура', 'Кашель у детей', 'Сыпь на коже', 'Проблемы со сном', 'Питание ребенка', 'Прививки'].map((topic) => (
                        <Chip
                          key={topic}
                          label={topic}
                          onClick={() => setChatInput(topic)}
                          sx={{
                            backgroundColor: '#E3F2FD',
                            color: '#1976D2',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#BBDEFB' }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  chatMessages.map((message) => (
                    <Fade key={message.id} in={true}>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                        mb: 1
                      }}>
                        <Paper sx={{
                          p: 2,
                          maxWidth: '80%',
                          backgroundColor: message.type === 'user' ? '#4A90E2' : '#F5F5F5',
                          color: message.type === 'user' ? 'white' : '#333',
                          borderRadius: 2,
                          whiteSpace: 'pre-line'
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {message.content}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            display: 'block', 
                            mt: 1, 
                            opacity: 0.7,
                            fontSize: '0.7rem'
                          }}>
                            {message.timestamp.toLocaleTimeString()}
                          </Typography>
                        </Paper>
                      </Box>
                    </Fade>
                  ))
                )}
                
                {isTyping && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Paper sx={{ p: 2, backgroundColor: '#F5F5F5', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          ИИ печатает
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Box sx={{ 
                            width: 4, 
                            height: 4, 
                            backgroundColor: '#4A90E2', 
                            borderRadius: '50%',
                            animation: 'pulse 1.4s ease-in-out infinite both'
                          }} />
                          <Box sx={{ 
                            width: 4, 
                            height: 4, 
                            backgroundColor: '#4A90E2', 
                            borderRadius: '50%',
                            animation: 'pulse 1.4s ease-in-out 0.2s infinite both'
                          }} />
                          <Box sx={{ 
                            width: 4, 
                            height: 4, 
                            backgroundColor: '#4A90E2', 
                            borderRadius: '50%',
                            animation: 'pulse 1.4s ease-in-out 0.4s infinite both'
                          }} />
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Box>
              
              {/* Поле ввода */}
              <Box sx={{ p: 2, borderTop: '1px solid #E0E0E0' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="Задайте вопрос о здоровье ребенка..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    multiline
                    maxRows={3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'white'
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={!chatInput.trim() || isTyping}
                    sx={{
                      backgroundColor: '#4A90E2',
                      borderRadius: 2,
                      px: 3,
                      minWidth: 'auto',
                      '&:hover': { backgroundColor: '#357ABD' }
                    }}
                  >
                    <SendIcon />
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      ) : (
        // Интерфейс анализа документов
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 3 }}>
        {/* Панель загрузки файлов */}
        <Box sx={{ flex: '1 1 400px' }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <FileUploadIcon sx={{ color: '#4A90E2', mr: 1, fontSize: 28 }} />
                <Typography variant="h3" sx={{ fontWeight: 600 }}>
                  Загрузка файлов
                </Typography>
              </Box>

              {/* Область загрузки */}
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '2px dashed #4A90E2',
                  borderRadius: 2,
                  backgroundColor: '#F8F9FA',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#E8F4FD',
                    borderColor: '#357ABD'
                  }
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadIcon sx={{ fontSize: 48, color: '#4A90E2', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, color: '#4A90E2' }}>
                  Перетащите файлы сюда или нажмите для выбора
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Поддерживаются: PDF, JPG, PNG, DOC, DOCX, TXT
                </Typography>
                <Button variant="contained" startIcon={<FileUploadIcon />}>
                  Выбрать файлы
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </Paper>

              {/* Список загруженных файлов */}
              {uploadedFiles.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Загруженные файлы ({uploadedFiles.length})
                  </Typography>
                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {uploadedFiles.map((file) => (
                      <Fade key={file.id} in={true}>
                        <Paper sx={{ 
                          p: 2, 
                          mb: 1, 
                          display: 'flex', 
                          alignItems: 'center',
                          borderRadius: 2,
                          '&:hover': { backgroundColor: '#F8F9FA' }
                        }}>
                          <Box sx={{ mr: 2 }}>
                            {file.type.startsWith('image/') ? (
                              <ImageIcon sx={{ color: '#4A90E2' }} />
                            ) : (
                              <DescriptionIcon sx={{ color: '#4A90E2' }} />
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600, 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              {(file.size / 1024).toFixed(1)} KB
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Анализировать">
                              <IconButton 
                                size="small" 
                                onClick={() => handleAnalyzeFile(file)}
                                sx={{ color: '#4A90E2' }}
                              >
                                <RobotIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Удалить">
                              <IconButton 
                                size="small" 
                                onClick={() => handleRemoveFile(file.id)}
                                sx={{ color: '#FF6B6B' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Paper>
                      </Fade>
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Текстовый ввод */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Или введите текст вручную
                </Typography>
                
                {/* Примеры для быстрого тестирования */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                    Примеры для тестирования:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip 
                      label="Анализ крови: гемоглобин 95, лейкоциты 15, СОЭ 25" 
                      onClick={() => setAnalysisText("Анализ крови: гемоглобин 95 г/л, лейкоциты 15×10⁹/л, СОЭ 25 мм/ч")}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                    <Chip 
                      label="Анализ мочи: белок 0.5, лейкоциты 20" 
                      onClick={() => setAnalysisText("Анализ мочи: белок 0.5 г/л, лейкоциты 20 в поле зрения, эритроциты 5 в поле зрения")}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                    <Chip 
                      label="Биохимия: АЛТ 80, АСТ 65, билирубин 35" 
                      onClick={() => setAnalysisText("Биохимический анализ: АЛТ 80 Ед/л, АСТ 65 Ед/л, билирубин 35 мкмоль/л, глюкоза 7.2 ммоль/л")}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                    <Chip 
                      label="Рентген: пневмония" 
                      onClick={() => setAnalysisText("Рентгенография органов грудной клетки: выявлены признаки пневмонии в нижней доле правого легкого")}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                    <Chip 
                      label="ЭКГ: синусовая тахикардия" 
                      onClick={() => setAnalysisText("ЭКГ: синусовая тахикардия, ритм 120 уд/мин, патологических изменений не выявлено")}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                  </Box>
                </Box>
                
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Вставьте результаты анализов или описание исследования..."
                  value={analysisText}
                  onChange={(e) => setAnalysisText(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleTextAnalysis}
                  disabled={!analysisText.trim() || isAnalyzing}
                  sx={{ 
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #4A90E2, #50C878)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #357ABD, #45B06A)',
                    }
                  }}
                >
                  Анализировать текст
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Панель результатов */}
        <Box sx={{ flex: '1 1 400px' }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CheckCircleIcon sx={{ color: '#4A90E2', mr: 1, fontSize: 28 }} />
                <Typography variant="h3" sx={{ fontWeight: 600 }}>
                  Результаты анализа
                </Typography>
              </Box>

              {isAnalyzing && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <RobotIcon sx={{ color: '#4A90E2', mr: 1 }} />
                    <Typography variant="h6" sx={{ color: '#4A90E2' }}>
                      ИИ анализирует документ...
                    </Typography>
                  </Box>
                  <LinearProgress sx={{ borderRadius: 1 }} />
                </Box>
              )}

              {analysisResults.length === 0 && !isAnalyzing && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <PsychologyIcon sx={{ fontSize: 64, color: '#E0E0E0', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#999', mb: 1 }}>
                    Нет результатов анализа
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Загрузите файл или введите текст для анализа
                  </Typography>
                </Box>
              )}

              {analysisResults.length > 0 && (
                <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {analysisResults.map((result, index) => (
                    <Fade key={result.id} in={true} timeout={300 + index * 100}>
                      <Paper sx={{ 
                        p: 3, 
                        mb: 2, 
                        borderRadius: 2,
                        border: `2px solid ${
                          result.status === 'success' ? '#50C878' :
                          result.status === 'warning' ? '#FFA726' : '#FF6B6B'
                        }`
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            backgroundColor: result.status === 'success' ? '#50C878' :
                                           result.status === 'warning' ? '#FFA726' : '#FF6B6B',
                            mr: 1
                          }} />
                          <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
                            {result.fileName}
                          </Typography>
                          <Chip 
                            label={`${result.confidence}%`} 
                            size="small" 
                            color={result.confidence > 80 ? 'success' : result.confidence > 60 ? 'warning' : 'error'}
                          />
                        </Box>
                        
                        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                          {result.timestamp.toLocaleString()}
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                          {result.analysis}
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#4A90E2' }}>
                            Рекомендации:
                          </Typography>
                          <List dense>
                            {result.recommendations.map((rec, i) => (
                              <ListItem key={i} sx={{ py: 0.5 }}>
                                <ListItemIcon>
                                  <CheckCircleIcon sx={{ color: '#50C878', fontSize: 16 }} />
                                </ListItemIcon>
                                <ListItemText primary={rec} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                          <Tooltip title="Копировать">
                            <IconButton 
                              size="small" 
                              onClick={() => copyToClipboard(`${result.analysis}\n\nРекомендации:\n${result.recommendations.map((rec, i) => `${i + 1}. ${rec}`).join('\n')}`)}
                            >
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Скачать">
                            <IconButton 
                              size="small" 
                              onClick={() => downloadResult(result)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    </Fade>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
      )}
    </Box>
  );
};

export default AIAssistant;
